import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { regionSkuQuery, type RegionSkuDistrict, type RegionSkuKind } from '../../api/easeedeskDashboards'
import { Content } from '../../ui/Shell'
import { RANGES, rangeOf, type RangeName } from './range'
import { NotInstalled } from './WallHeader'

/**
 * "TDS Performance Dashboard" - cele patru ecrane ale lui
 * `easeedesk-dashboards/Region_sku` (`index`, `new_pc_services`,
 * `existing_pc_services`, `all_sku`), machetele
 * `views/version-3/dashboards/easeedesk/sku/region.php` si `all_sku.php`.
 *
 * Cele patru se deosebeau doar prin `filterType` si prin titlu; cifrele
 * veneau din acelasi capat, si vin si aici. Un singur ecran, cu felul in
 * ruta.
 *
 * Ce se vede: numarul total de servicii, si tabelul cu o coloana pe regiune -
 * numele si totalul in cap, districtele dedesubt, in ordinea numarului. Un
 * district se apasa si arata magazinele lui, ca `#storesModal` din macheta.
 *
 * CELE TREI PANOURI DE GRAFIC NU S-AU ADUS, si nu e o scapare: erau DEJA
 * stinse in portal. In `public/js/dashboards/easeedesk.js`, `drawTotalChart`,
 * `drawTopDistrictsChart` si `drawTopWorstDistrictsChart` sunt comentate, si
 * la fel sunt si chemarile lor din `buildDashboard` - deci `#totalchart`,
 * `#topdistrictschart` si `#topworstdistrctschart` sunt goale si azi. amCharts
 * a iesit din portal pe 6 septembrie 2026 si nu se intoarce pentru trei casute
 * goale.
 */

const STYLE = `
.od-region-sku .od-total {
  text-align: right; background: #1F62CB; color: #fff; font-size: 32px; padding: 20px;
  border-right: 12px solid #fff; width: 50%;
}
.od-region-sku .district { cursor: pointer; padding: 2px 5px; border-bottom: 1px solid #ddd; }
.od-region-sku #totaltable td { vertical-align: top; padding: 0; }
.od-region-sku #totaltable thead td { padding: 5px; }
`

export function RegionSkuPage({ kind }: { kind: RegionSkuKind }) {
  const [rangeName, setRangeName] = useState<RangeName>('Today')
  const [open, setOpen] = useState<RegionSkuDistrict | null>(null)
  const q = useQuery(regionSkuQuery(kind, rangeOf(rangeName)))
  const page = q.data
  const regions = page?.regions ?? []

  return (
    <Content>
      <style>{STYLE}</style>
      <div className="od-region-sku">
        <div className="row" style={{ margin: 0 }}>
          <div className="col-md-6">
            <h4>{page?.title ?? 'TDS Performance Dashboard'}</h4>
          </div>
          <div className="col-md-6">
            <div className="pull-right">
              <select
                className="form-control"
                aria-label="Date range"
                value={rangeName}
                onChange={(e) => setRangeName(e.target.value as RangeName)}
              >
                {RANGES.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {page && !page.ready ? <NotInstalled what="TDS Performance" /> : null}

        <div className="row" style={{ margin: 0, marginTop: 50 }}>
          <div className="col-md-4">
            <table width="100%">
              <tbody>
                <tr>
                  <td className="od-total">
                    <span id="totalservices">{page?.total ?? 0}</span>
                    <p>total services</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="col-md-8">
            <table className="table table-bordered" id="totaltable">
              <thead>
                <tr>
                  {regions.map((region) => (
                    <td key={region.region_id}>
                      {region.region_name}{' '}
                      <span className="label label-default pull-right">{region.total}</span>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {regions.map((region) => (
                    <td key={region.region_id}>
                      {region.districts.map((district) => (
                        <div
                          key={district.id}
                          className="district"
                          role="button"
                          tabIndex={0}
                          onClick={() => setOpen(district)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') setOpen(district)
                          }}
                        >
                          {district.id}
                          <span className="label label-default" style={{ float: 'right' }}>
                            {district.total}
                          </span>
                        </div>
                      ))}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {open ? (
          <div className="modal fade in" id="storesModal" style={{ display: 'block' }} role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <button type="button" className="close" aria-label="Close" onClick={() => setOpen(null)}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                  <h4 className="modal-title">{`District stores ${open.id}`}</h4>
                </div>
                <div className="modal-body">
                  {open.stores.map((store) => (
                    <div key={store.id} style={{ padding: 10 }}>
                      <label className="label label-primary" style={{ marginRight: 20 }}>
                        {store.total}
                      </label>{' '}
                      {`Store ${store.id}`}
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={() => setOpen(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Content>
  )
}
